(() => {
  if (window.UGJPVideoTracking) return;

  const consumed = new Set();
  const attached = new WeakMap();
  const sdkPromises = new Map();
  const allowed = () => Boolean(window.UGJPTracking?.allowed());

  function sourceOf(element) {
    try {
      const source = element.getAttribute('src') || element.querySelector('source')?.getAttribute('src') || element.currentSrc;
      if (!source) return null;
      const url = new URL(source, window.location.href);
      if (!/^https?:$/.test(url.protocol)) return null;
      if (element.tagName === 'IFRAME') {
        const host = url.hostname.toLowerCase();
        if (/(^|\.)(youtube\.com|youtube-nocookie\.com)$/.test(host)) {
          const id = url.pathname.match(/^\/embed\/([\w-]+)$/)?.[1];
          return id ? { provider: 'youtube', id: 'youtube:' + id } : null;
        }
        if (host === 'player.vimeo.com') {
          const id = url.pathname.match(/^\/video\/(\d+)/)?.[1];
          return id ? { provider: 'vimeo', id: 'vimeo:' + id } : null;
        }
        return null;
      }
      return element.tagName === 'VIDEO' ? { provider: 'html5', id: 'html5:' + url.origin + url.pathname } : null;
    } catch {
      return null;
    }
  }

  function loadSDK(provider) {
    const ready = () => provider === 'youtube' ? window.YT?.Player : window.Vimeo?.Player;
    if (ready()) return Promise.resolve();
    if (!allowed()) return Promise.reject(new Error('Tracking consent is unavailable'));
    if (sdkPromises.has(provider)) return sdkPromises.get(provider);
    const url = provider === 'youtube' ? 'https://www.youtube.com/iframe_api' : 'https://player.vimeo.com/api/player.js';
    const promise = new Promise((resolve, reject) => {
      let poll;
      let timer;
      let script = Array.from(document.scripts).find((item) => item.src === url);
      const finish = (error) => {
        clearInterval(poll);
        clearTimeout(timer);
        script?.removeEventListener('error', failed);
        if (error) reject(error);
        else resolve();
      };
      const failed = () => finish(new Error('Video tracking SDK failed to load'));
      poll = setInterval(() => { if (ready()) finish(); }, 100);
      timer = setTimeout(failed, 15000);
      if (!script) {
        script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.dataset.jpVideoSdk = provider;
        script.addEventListener('error', failed, { once: true });
        document.head.appendChild(script);
      } else {
        script.addEventListener('error', failed, { once: true });
      }
    });
    sdkPromises.set(provider, promise);
    return promise;
  }

  function attach(element, context = {}) {
    if (!element || !['VIDEO', 'IFRAME'].includes(element.tagName)) return () => {};
    if (element.classList.contains('ug-jpcm__video')) return () => {};
    if (attached.has(element)) return attached.get(element);
    const source = sourceOf(element);
    if (!source) return () => {};

    const controller = new AbortController();
    const signal = controller.signal;
    const info = { ...context, video_provider: source.provider };
    let alive = true;
    let permitted = allowed();
    let baselinePending = !permitted;
    let playing = false;
    let seeking = false;
    let previous = null;
    let tail = 0;
    let duration = 0;
    let sdkPlayer;
    let sdkStarting = false;
    let poll;
    let removeSDKListeners = () => {};

    function consume(node, eventName, percent, suppress = false) {
      const key = 'video:' + String(info.video_id || 'video') + ':' + source.id + ':' + node;
      if (consumed.has(key)) return;
      consumed.add(key);
      if (!suppress && allowed() && alive) {
        try { window.UGJPTracking?.track(eventName, { ...info, video_percent: percent }, key); } catch {}
      }
    }

    function markBaseline(time, length, hasPlayed) {
      if (hasPlayed || time > 0) consume('start', 'jp_sp_video_start', 0, true);
      if (Number.isFinite(length) && length > 0) {
        [25, 50, 75].forEach((percent) => {
          if (time / length * 100 >= percent) consume(percent, 'jp_sp_video_progress', percent, true);
        });
        if (time >= length) consume('complete', 'jp_sp_video_complete', 100, true);
      }
      baselinePending = false;
      previous = null;
      tail = 0;
    }

    function permission(time, length, hasPlayed) {
      const next = allowed();
      if (next !== permitted) {
        baselinePending = true;
        previous = null;
        tail = 0;
        permitted = next;
      }
      if (baselinePending && Number.isFinite(time)) markBaseline(time, length, hasPlayed);
      return next;
    }

    function sample(time, length, isPlaying, rate = 1) {
      if (!alive || !Number.isFinite(time) || !Number.isFinite(length) || length <= 0) return;
      permission(time, length, playing || isPlaying);
      duration = length;
      const now = performance.now();
      if (previous && isPlaying && playing && !seeking && permitted) {
        const delta = time - previous.time;
        const expected = (now - previous.wall) / 1000 * Math.max(0.25, Number(rate) || 1);
        if (delta >= 0 && delta <= expected + 0.4 && now - previous.wall < 2500) tail += delta;
        else tail = 0;
      } else {
        tail = 0;
      }
      previous = { time, wall: now };
      if (isPlaying && !seeking) {
        [25, 50, 75].forEach((percent) => {
          if (time / length * 100 >= percent) consume(percent, 'jp_sp_video_progress', percent);
        });
      }
      playing = isPlaying;
    }

    function started(time, length, rate = 1) {
      if (!alive) return;
      permission(time, length, true);
      consume('start', 'jp_sp_video_start', 0);
      sample(time, length, true, rate);
      playing = true;
    }

    function stopped() {
      playing = false;
      previous = null;
      tail = 0;
    }

    function seek() {
      seeking = true;
      previous = null;
      tail = 0;
    }

    function seeked() {
      seeking = false;
      previous = null;
      tail = 0;
    }

    function ended(time, length, rate = 1) {
      if (!alive) return;
      const wasPlaying = playing;
      sample(time, length, wasPlaying, rate);
      const required = Math.min(2, duration * 0.2);
      if (wasPlaying && !seeking && duration > 0 && time >= duration - 0.25 && tail >= required) {
        consume('complete', 'jp_sp_video_complete', 100);
      } else if (!allowed()) {
        consume('complete', 'jp_sp_video_complete', 100, true);
      }
      stopped();
    }

    if (source.provider === 'html5') {
      element.addEventListener('playing', () => started(element.currentTime, element.duration, element.playbackRate), { signal });
      element.addEventListener('timeupdate', () => {
        if (!element.ended) sample(element.currentTime, element.duration, playing && !element.paused, element.playbackRate);
      }, { signal });
      element.addEventListener('pause', () => { if (!element.ended) stopped(); }, { signal });
      element.addEventListener('waiting', stopped, { signal });
      element.addEventListener('seeking', seek, { signal });
      element.addEventListener('seeked', seeked, { signal });
      element.addEventListener('ended', () => ended(element.currentTime, element.duration, element.playbackRate), { signal });
      element.addEventListener('emptied', stopped, { signal });
    }

    function readYouTube() {
      try {
        const state = sdkPlayer.getPlayerState();
        return { time: sdkPlayer.getCurrentTime(), duration: sdkPlayer.getDuration(), playing: state === 1, rate: sdkPlayer.getPlaybackRate() };
      } catch {
        return null;
      }
    }

    function youtube() {
      let ready = false;
      sdkPlayer = new window.YT.Player(element, {
        events: {
          onReady: () => {
            if (!alive) return;
            ready = true;
            const value = readYouTube();
            if (!value) return;
            permission(value.time, value.duration, value.playing);
            if (value.playing) started(value.time, value.duration, value.rate);
          },
          onStateChange: (event) => {
            if (!alive || !ready) return;
            const value = readYouTube();
            if (!value) return;
            if (event.data === 1) started(value.time, value.duration, value.rate);
            else if (event.data === 0) ended(value.time, value.duration, value.rate);
            else stopped();
          }
        }
      });
      poll = setInterval(() => {
        if (!alive || !ready || !allowed()) return;
        const value = readYouTube();
        if (value?.playing) sample(value.time, value.duration, true, value.rate);
      }, 500);
    }

    function vimeo() {
      sdkPlayer = new window.Vimeo.Player(element);
      let ready = false;
      let rate = 1;
      let resumeFrom = null;
      let pendingPlaying = null;
      const pause = () => {
        resumeFrom = null;
        stopped();
      };
      const resume = () => {
        if (!ready || !alive) return;
        Promise.all([sdkPlayer.getCurrentTime(), sdkPlayer.getPaused()]).then(([time, paused]) => {
          if (!alive || paused || playing) return;
          resumeFrom = time;
        }).catch(() => {});
      };
      const listeners = {
        playing: (value) => {
          resumeFrom = null;
          if (ready) started(value.seconds, value.duration, rate);
          else pendingPlaying = value;
        },
        timeupdate: (value) => {
          if (!ready) return;
          if (resumeFrom !== null && value.seconds > resumeFrom && !seeking) {
            resumeFrom = null;
            started(value.seconds, value.duration, rate);
          } else {
            sample(value.seconds, value.duration, playing, rate);
          }
        },
        pause,
        bufferstart: pause,
        bufferend: resume,
        seeking: seek,
        seeked,
        ended: (value) => { if (ready) ended(value.seconds, value.duration, rate); },
        playbackratechange: (value) => { rate = value.playbackRate || 1; }
      };
      Object.entries(listeners).forEach(([name, callback]) => sdkPlayer.on(name, callback));
      removeSDKListeners = () => Object.entries(listeners).forEach(([name, callback]) => sdkPlayer.off(name, callback));
      sdkPlayer.ready().then(() => Promise.all([sdkPlayer.getCurrentTime(), sdkPlayer.getDuration(), sdkPlayer.getPaused(), sdkPlayer.getPlaybackRate()])).then(([time, length, paused, playbackRate]) => {
        if (!alive) return;
        rate = playbackRate;
        permission(time, length, !paused);
        ready = true;
        if (!paused && pendingPlaying) started(time, length, rate);
        else if (!paused) resumeFrom = time;
        pendingPlaying = null;
      }).catch(() => {});
    }

    function startSDK() {
      if (!alive || sdkStarting || sdkPlayer || !allowed() || source.provider === 'html5') return;
      sdkStarting = true;
      loadSDK(source.provider).then(() => {
        if (!alive || !allowed()) return;
        if (!element.isConnected) return;
        if (source.provider === 'youtube') youtube();
        else vimeo();
      }).catch(() => {}).finally(() => { sdkStarting = false; });
    }

    document.addEventListener('jp:tracking-consent', () => {
      if (!alive) return;
      const next = allowed();
      if (next !== permitted) {
        permitted = next;
        baselinePending = true;
        stopped();
      }
      if (source.provider === 'html5') {
        markBaseline(element.currentTime, element.duration, !element.paused || element.currentTime > 0);
        playing = !element.paused && !element.ended;
      } else if (sdkPlayer && next) {
        if (source.provider === 'youtube') {
          const value = readYouTube();
          if (value) {
            markBaseline(value.time, value.duration, value.playing);
            playing = value.playing;
          }
        } else {
          Promise.all([sdkPlayer.getCurrentTime(), sdkPlayer.getDuration(), sdkPlayer.getPaused()]).then(([time, length, paused]) => {
            if (!alive) return;
            markBaseline(time, length, !paused);
            playing = !paused;
          }).catch(() => {});
        }
      }
      startSDK();
    }, { signal });

    if (source.provider !== 'html5') {
      element.addEventListener('load', startSDK, { signal });
      queueMicrotask(startSDK);
    }

    function cleanup() {
      if (!alive) return;
      alive = false;
      clearInterval(poll);
      controller.abort();
      try { removeSDKListeners(); } catch {}
      attached.delete(element);
      if (source.provider === 'youtube') {
        try { sdkPlayer?.destroy(); } catch {}
      } else if (source.provider === 'vimeo') {
        try { sdkPlayer?.destroy()?.catch(() => {}); } catch {}
      }
    }

    attached.set(element, cleanup);
    return cleanup;
  }

  window.UGJPVideoTracking = { attach };
})();
